import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createSftpServerMaster, updateSftpServerMaster, getSftpServerMasterById } from '../services/api';
import { toast } from 'react-toastify';
import './MasterForm.css';

const SftpServerMasterForm = () => {
  const navigate = useNavigate();
  const { serverId } = useParams();
  const isEdit = Boolean(serverId);
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authMethod, setAuthMethod] = useState('password');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    host: '',
    port: 22,
    username: '',
    password: '',
    privateKey: '',
    remotePath: '/backups',
    isActive: true,
    priority: 1
  });

  useEffect(() => {
    if (isEdit) {
      fetchServer();
    }
  }, [serverId]);

  const fetchServer = async () => {
    try {
      setLoading(true);
      const response = await getSftpServerMasterById(serverId);
      setFormData(prev => ({
        ...prev,
        ...response.data,
        password: '',
        privateKey: ''
      }));
      setAuthMethod(response.data.privateKey ? 'key' : 'password');
    } catch (error) {
      toast.error(error.message || 'Failed to fetch SFTP server');
      navigate('/sftp-servers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const handleAuthMethodChange = (method) => {
    setAuthMethod(method);
    if (method === 'password') {
      setFormData(prev => ({ ...prev, privateKey: '' }));
    } else {
      setFormData(prev => ({ ...prev, password: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Remove empty password/key fields for edit
      if (isEdit) {
        if (!submitData.password) delete submitData.password;
        if (!submitData.privateKey) delete submitData.privateKey;
      }

      if (isEdit) {
        await updateSftpServerMaster(serverId, submitData);
        toast.success('SFTP server updated successfully');
      } else {
        await createSftpServerMaster(submitData);
        toast.success('SFTP server created successfully');
      }
      navigate('/sftp-servers');
    } catch (error) {
      toast.error(error.message || 'Failed to save SFTP server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="master-form-container">
      <div className="form-header">
        <h1>{isEdit ? 'Edit SFTP Server' : 'Create SFTP Server'}</h1>
        <p>Configure a reusable SFTP server connection</p>
      </div>

      <form onSubmit={handleSubmit} className="master-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="name">Server Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Primary Backup Server"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Optional description"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Connection Details</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="host">Host *</label>
              <input
                type="text"
                id="host"
                name="host"
                value={formData.host}
                onChange={handleChange}
                required
                placeholder="sftp.example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="port">Port *</label>
              <input
                type="number"
                id="port"
                name="port"
                value={formData.port}
                onChange={handleChange}
                required
                min="1"
                max="65535"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Authentication Method</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  checked={authMethod === 'password'}
                  onChange={() => handleAuthMethodChange('password')}
                />
                Password
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  checked={authMethod === 'key'}
                  onChange={() => handleAuthMethodChange('key')}
                />
                Private Key
              </label>
            </div>
          </div>

          {authMethod === 'password' ? (
            <div className="form-group">
              <label htmlFor="password">Password {!isEdit && '*'}</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!isEdit}
                  placeholder={isEdit ? 'Leave blank to keep current' : ''}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="privateKey">Private Key {!isEdit && '*'}</label>
              <textarea
                id="privateKey"
                name="privateKey"
                value={formData.privateKey}
                onChange={handleChange}
                required={!isEdit}
                rows="6"
                placeholder={isEdit ? 'Leave blank to keep current' : '-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="remotePath">Remote Path *</label>
            <input
              type="text"
              id="remotePath"
              name="remotePath"
              value={formData.remotePath}
              onChange={handleChange}
              required
              placeholder="/backups"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priority">Priority *</label>
              <input
                type="number"
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                required
                min="1"
              />
              <small>Lower number = higher priority</small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                Active
              </label>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/sftp-servers')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : isEdit ? 'Update Server' : 'Create Server'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SftpServerMasterForm;